import React, { Fragment } from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";

const createHelpers = (width, height, css) => {
    // somehow sizes is ending up in markup, even if it is not a valid svg attribute
    // until we have a better solution, just render it empty, instead to '[Object object]'
    const sanitizeSizes = sizes =>
        Object.defineProperty(sizes, "toString", {
            value: () => "",
            enumerable: false
        });

    const getDimensions = (size, sizes) => {
        if (
            size &&
            typeof size.width === "number" &&
            typeof size.height === "number"
        ) {
            return size;
        }

        return size && sizes[size] ? sizes[size] : { width, height };
    };

    const getCss = (size, sizes, fillColor, fillColorRule, noStyles) => {
        if (noStyles) {
            return "";
        }

        const dimensions = getDimensions(size, sizes);
        const fillRule =
            fillColor && fillColorRule
                ? `${fillColorRule}{ fill: ${fillColor}; }`
                : "";

        return css`
            width: ${dimensions.width}px;
            height: ${dimensions.height}px;
            ${fillRule}
        `;
    };

    const propsToCss = ({ size, sizes, fillColor, fillColorRule, noStyles }) =>
        getCss(size, sizes, fillColor, fillColorRule, noStyles);

    return {
        getCss,
        getDimensions,
        propsToCss,
        sanitizeSizes
    };
};

const width = "24";
const height = "24";
const viewBox = "0 0 24 24";

const { getDimensions, getCss, propsToCss, sanitizeSizes } = createHelpers(
    width,
    height,
    css
);

const sizes = sanitizeSizes({
    small: { width: 17, height: 17 },
    medium: { width: 24, height: 24 }
});

const Image = styled.svg`
    ${propsToCss}
`;

const children = (
    <Fragment>
        <path
            fill="#000"
            fillRule="nonzero"
            d="M12.75.75v3a.75.75 0 01-1.5 0v-3a.75.75 0 111.5 0zM7.024 1.882a.75.75 0 10-1.298.75l1.5 2.598a.75.75 0 101.298-.75l-1.5-2.598zM5.23 7.226l-2.598-1.5a.75.75 0 00-.75 1.298l2.598 1.5a.75.75 0 00.75-1.298zM3.75 11.25h-3a.75.75 0 100 1.5h3a.75.75 0 000-1.5zM12 19.5a.75.75 0 00-.75.75v3a.75.75 0 001.5 0v-3a.75.75 0 00-.75-.75zm4.774-.73a.75.75 0 10-1.299.75l1.5 2.598a.75.75 0 101.3-.75l-1.5-2.598zm5.344-1.794l-2.598-1.5a.75.75 0 00-.75 1.298l2.598 1.5a.75.75 0 00.75-1.299zm1.132-5.726h-3a.75.75 0 100 1.5h3a.75.75 0 000-1.5zm-3.73-2.726l2.598-1.5a.75.75 0 10-.75-1.298l-2.598 1.5a.75.75 0 10.75 1.298zM18 1.608a.75.75 0 00-1.024.274l-1.5 2.598a.75.75 0 101.298.75l1.5-2.598A.75.75 0 0018 1.608z"
            key="key-0"
        />
    </Fragment>
);

const defaultProps = {
    children,
    viewBox,
    fillColor: null,
    fillColorRule: "&&& path, &&& use, &&& g",
    sizes,
    size: null
};

Image.propTypes /* remove-proptypes */ = {
    fillColor: PropTypes.string,
    fillColorRule: PropTypes.string,
    viewBox: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
    size: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
            height: PropTypes.number.isRequired,
            width: PropTypes.number.isRequired
        })
    ]),
    sizes: PropTypes.shape({
        height: PropTypes.number,
        width: PropTypes.number
    })
};

export default Object.assign(Image, {
    getDimensions,
    getCss,
    defaultProps,
    displayName: "Spinner"
});
